import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Typography,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StopIcon from "@mui/icons-material/Stop";
import ReplayIcon from "@mui/icons-material/Replay";
import { extractorApi, PdfJob, PdfJobConflict } from "../services/extractorApi";
import { PdfExtractionReportModal } from "./PdfExtractionReportModal";
import toast from "react-hot-toast";

/** Checkpoints shown in the progress stepper, in execution order. Labels are
 * intentionally generic — no underlying tool/model names are ever surfaced. */
const CHECKPOINTS: { key: PdfJob["status"]; label: string }[] = [
  { key: "rendering", label: "Preparazione" },
  { key: "extracting", label: "Analisi" },
  { key: "auditing", label: "Verifica" },
  { key: "compacting", label: "Ottimizzazione" },
  { key: "writing", label: "File Excel" },
];

const ACTIVE_STATUSES: PdfJob["status"][] = [
  "queued", "rendering", "extracting", "auditing", "compacting", "writing",
];

const POLL_INTERVAL_MS = 2500;

function activeCheckpointIndex(status: PdfJob["status"]): number {
  if (status === "done") return CHECKPOINTS.length;
  const idx = CHECKPOINTS.findIndex((c) => c.key === status);
  return idx; // -1 for "queued" or "failed" → nothing completed yet
}

/** "circa 7 minuti", "meno di un minuto", "circa 1 ora e 10 minuti". */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  if (seconds < 60) return "meno di un minuto";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `circa ${minutes} ${minutes === 1 ? "minuto" : "minuti"}`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = `${h} ${h === 1 ? "ora" : "ore"}`;
  return m === 0 ? `circa ${hours}` : `circa ${hours} e ${m} ${m === 1 ? "minuto" : "minuti"}`;
}

function statusChipColor(status: PdfJob["status"]): "default" | "success" | "error" | "warning" {
  if (status === "done") return "success";
  if (status === "failed" || status === "cancelled") return "error";
  if (ACTIVE_STATUSES.includes(status)) return "warning";
  return "default";
}

/** Content rendered inside ToolModal for the "Convertitore PDF File di Gara" tool. */
export const PdfConverterContent: React.FC = () => {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [job, setJob] = useState<PdfJob | null>(null);
  const [loadingCurrent, setLoadingCurrent] = useState(true);
  const [starting, setStarting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [history, setHistory] = useState<PdfJob[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [resumingId, setResumingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isActive = !!job && ACTIVE_STATUSES.includes(job.status);

  const refreshHistory = useCallback(() => {
    extractorApi.listPdfJobs().then(setHistory).catch(() => {});
  }, []);

  // On mount: restore any in-progress (or most recently finished) job so the
  // user can close the browser mid-conversion and come back to see it live.
  useEffect(() => {
    let cancelled = false;
    extractorApi
      .getCurrentPdfJob()
      .then((current) => {
        // Only auto-restore a job that's still running — a past done/failed
        // job stays visible in the history list instead of blocking the
        // upload form on every visit.
        // A failed job that can still be resumed is restored too: the user
        // can pick it up from where it stopped instead of starting over.
        if (
          !cancelled && current &&
          (ACTIVE_STATUSES.includes(current.status) || (current.status === "failed" && current.can_resume))
        ) {
          setJob(current);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingCurrent(false);
      });
    refreshHistory();
    return () => {
      cancelled = true;
    };
  }, [refreshHistory]);

  // Poll while a job is active.
  useEffect(() => {
    if (!job || !ACTIVE_STATUSES.includes(job.status)) return;

    const tick = async () => {
      try {
        const updated = await extractorApi.getPdfJob(job.job_id);
        setJob(updated);
        if (updated.status === "done" || updated.status === "failed" || updated.status === "cancelled") {
          refreshHistory();
          if (updated.status === "done") setReportOpen(true);
        }
      } catch {
        // transient polling error — silently retry on the next tick
      }
    };

    pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, refreshHistory]);

  const isPdfFile = (f: File) =>
    f.name.toLowerCase().endsWith(".pdf") || f.type.toLowerCase() === "application/pdf";

  const accept = (f: File) => {
    if (isPdfFile(f)) {
      setPdfFile(f);
      setErrorMsg(null);
      return;
    }
    toast.error("Seleziona un file PDF (.pdf)");
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleStart = async () => {
    if (!pdfFile) return;
    setStarting(true);
    setErrorMsg(null);
    try {
      const started = await extractorApi.startPdfJob(pdfFile);
      setJob(started);
      setPdfFile(null);
    } catch (e: any) {
      if (e?.response?.status === 409) {
        const detail = e.response.data?.detail as PdfJobConflict | undefined;
        if (detail?.job) {
          setJob(detail.job);
          toast.error("Hai già una conversione in corso: eccone lo stato.");
        } else {
          setErrorMsg("Hai già una conversione PDF in corso.");
        }
      } else {
        const detail: string =
          e?.response?.data?.detail || "Errore nell'avvio della conversione.";
        setErrorMsg(detail);
      }
    } finally {
      setStarting(false);
    }
  };

  const handleDownload = async (targetJob: PdfJob) => {
    try {
      await extractorApi.downloadPdfJobResult(targetJob.job_id, targetJob.filename);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Download non riuscito.");
      refreshHistory();
    }
  };

  const handleDelete = async (targetJob: PdfJob) => {
    if (!window.confirm(`Eliminare definitivamente "${targetJob.filename}" dalla cronologia?`)) return;
    setDeletingId(targetJob.job_id);
    try {
      await extractorApi.deletePdfJob(targetJob.job_id);
      setHistory((prev) => prev.filter((h) => h.job_id !== targetJob.job_id));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Impossibile eliminare la conversione.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancel = async () => {
    if (!job || !ACTIVE_STATUSES.includes(job.status)) return;
    if (!window.confirm("Interrompere la conversione in corso?")) return;
    setCancelling(true);
    try {
      const updated = await extractorApi.cancelPdfJob(job.job_id);
      setJob(updated);
      refreshHistory();
      toast.success("Conversione interrotta.");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Impossibile interrompere la conversione.");
    } finally {
      setCancelling(false);
    }
  };

  const handleResume = async (target: PdfJob) => {
    setResumingId(target.job_id);
    try {
      const resumed = await extractorApi.resumePdfJob(target.job_id);
      setJob(resumed);
      setReportOpen(false);
      refreshHistory();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.job) {
        setJob(detail.job);
        toast.error("Hai già una conversione in corso: eccone lo stato.");
      } else {
        toast.error((typeof detail === "string" && detail) || "Impossibile riprendere la conversione.");
      }
      refreshHistory();
    } finally {
      setResumingId(null);
    }
  };

  const handleReset = () => {
    setJob(null);
    setPdfFile(null);
    setErrorMsg(null);
    setReportOpen(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  if (loadingCurrent) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const activeIdx = job ? activeCheckpointIndex(job.status) : -1;

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: 560,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          py: 2,
          px: { xs: 2, sm: 0 },
        }}
      >
        {/* ── Active / finished job view ───────────────────────────────── */}
        {job && (isActive || job.status === "done" || job.status === "failed" || job.status === "cancelled") ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <InsertDriveFileIcon sx={{ color: "#C62828" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {job.filename}
                </Typography>
                <Chip
                  size="small"
                  label={
                    job.status === "done"
                      ? "Completata"
                      : job.status === "failed"
                        ? "Non riuscita"
                        : job.status === "cancelled"
                          ? "Interrotta"
                          : job.stage_label
                  }
                  color={statusChipColor(job.status)}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>

            {isActive && (
              <>
                {job.estimated_seconds != null && (job.status === "queued" || job.status === "rendering") && (
                  <Alert severity="info" icon={false} sx={{ borderRadius: "10px" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Tempo stimato per il completamento: {formatDuration(job.estimated_seconds)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {job.total_pages ? `Il documento ha ${job.total_pages} pagine. ` : ""}
                      La stima si aggiorna man mano che l'analisi procede.
                    </Typography>
                  </Alert>
                )}
                <Stepper activeStep={activeIdx} alternativeLabel sx={{ my: 1 }}>
                  {CHECKPOINTS.map((c) => (
                    <Step key={c.key}>
                      <StepLabel>{c.label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {job.stage_label}
                      {job.status === "extracting" && job.total_units > 0
                        ? ` · ${job.completed_units}/${job.total_units} sezioni analizzate`
                        : ""}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {job.progress_pct}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={job.progress_pct}
                    color="error"
                    sx={{ borderRadius: 2, height: 8 }}
                  />
                </Box>
                {(job.total_pages || job.estimated_remaining_seconds != null) && (
                  <Typography variant="body2" sx={{ textAlign: "center", fontWeight: 500 }}>
                    {job.total_pages ? `${job.total_pages} pagine` : ""}
                    {job.total_pages && job.estimated_remaining_seconds != null ? " · " : ""}
                    {job.estimated_remaining_seconds != null
                      ? `tempo rimanente stimato: ${formatDuration(job.estimated_remaining_seconds)}`
                      : ""}
                  </Typography>
                )}
                {job.resumed_units > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    Ripresa: {job.resumed_units} sezioni già analizzate non vengono rifatte.
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                  Puoi chiudere questa schermata: la conversione continua in background.
                  Torna qui in qualsiasi momento per vedere l'avanzamento.
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
                    disabled={cancelling}
                    onClick={handleCancel}
                  >
                    Interrompi
                  </Button>
                </Box>
              </>
            )}

            {job.status === "failed" && (
              <Alert severity="error" onClose={handleReset}>
                <AlertTitle>Conversione non riuscita</AlertTitle>
                {job.error || "Si è verificato un errore imprevisto."}
                {job.can_resume && (
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                    Il lavoro già svolto è stato salvato: con "Riprendi" l'analisi riparte da dove si era fermata.
                  </Typography>
                )}
              </Alert>
            )}

            {job.status === "cancelled" && (
              <Alert severity="warning" onClose={handleReset}>
                <AlertTitle>Conversione interrotta</AlertTitle>
                Puoi caricare di nuovo lo stesso file o un altro PDF.
              </Alert>
            )}

            {job.status === "done" && job.report && (
              <Alert
                severity={
                  job.report.windows_failed > 0 ||
                  job.report.audit_missing.length > 0 ||
                  (job.report.review_alerts?.length ?? 0) > 0
                    ? "warning"
                    : "success"
                }
                sx={{ borderRadius: "10px", cursor: "pointer" }}
                onClick={() => setReportOpen(true)}
              >
                <AlertTitle>Conversione completata</AlertTitle>
                {job.report.total_items} voci estratte in {job.report.total_chapters} capitoli
                {job.report.windows_failed > 0 && ` · ${job.report.windows_failed} finestre da verificare`}
                {job.report.audit_missing.length > 0 && ` · ${job.report.audit_missing.length} progressivi mancanti`}
                {(job.report.review_alerts?.length ?? 0) > 0 &&
                  ` · ${job.report.review_alerts!.length} righe da verificare`}
                . Clicca per i dettagli.
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
              {job.can_resume && (
                <Button
                  variant={job.status === "failed" ? "contained" : "outlined"}
                  color="error"
                  startIcon={resumingId === job.job_id ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
                  disabled={resumingId === job.job_id}
                  onClick={() => handleResume(job)}
                >
                  {job.status === "failed" ? "Riprendi" : "Ritenta le sezioni mancanti"}
                </Button>
              )}
              {job.status === "done" && (
                <>
                  <Button variant="text" onClick={() => setReportOpen(true)}>
                    Vedi report
                  </Button>
                  <Button variant="outlined" onClick={handleReset}>
                    Nuovo file
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DownloadIcon />}
                    disabled={!job.can_download}
                    onClick={() => handleDownload(job)}
                  >
                    Scarica Excel
                  </Button>
                </>
              )}
              {(job.status === "failed" || job.status === "cancelled") && (
                <Button variant={job.can_resume ? "outlined" : "contained"} color="error" onClick={handleReset}>
                  {job.can_resume ? "Carica un altro file" : "Riprova con un nuovo file"}
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <>
            {/* ── Drop zone ─────────────────────────────────────────────── */}
            <Box
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const f = e.dataTransfer.files?.[0];
                if (f) accept(f);
              }}
              sx={{
                border: "2px dashed",
                borderColor: dragging ? "#C62828" : pdfFile ? "#C6282840" : "divider",
                borderRadius: "12px",
                p: 5,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragging ? "#C6282808" : pdfFile ? "#C6282805" : "#FFFFFF",
                transition: "all 0.2s",
                "&:hover": { borderColor: "#C6282860", bgcolor: "#C6282808" },
              }}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,application/pdf"
                style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) accept(f); }}
              />
              {pdfFile ? (
                <>
                  <InsertDriveFileIcon sx={{ fontSize: "2.8rem", color: "#C62828", mb: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {pdfFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(pdfFile.size / 1024).toFixed(0)} KB — clicca per cambiare
                  </Typography>
                </>
              ) : (
                <>
                  <PictureAsPdfIcon sx={{ fontSize: "2.8rem", color: "text.disabled", mb: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Trascina qui il PDF del file di gara
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    oppure clicca per sfogliare — solo .pdf
                  </Typography>
                </>
              )}
            </Box>

            {errorMsg && (
              <Alert severity="error" onClose={() => setErrorMsg(null)}>
                <AlertTitle>Impossibile avviare la conversione</AlertTitle>
                {errorMsg}
              </Alert>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="error"
                disabled={!pdfFile || starting}
                onClick={handleStart}
                startIcon={starting ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                sx={{ minWidth: 200 }}
              >
                {starting ? "Avvio in corso…" : "Avvia conversione"}
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
              L'elaborazione richiede circa 8-9 secondi a pagina: dopo il caricamento ti mostriamo il tempo
              stimato per il tuo documento. Una volta avviata puoi chiudere questa schermata e tornare più tardi per vedere l'avanzamento.
              L'Excel segue il formato IMPORT standard (Capitolo, Codice, Descrizione breve/estesa, UdM, Quantità).
            </Typography>
          </>
        )}

        {/* ── History ───────────────────────────────────────────────────── */}
        {history.length > 0 && (
          <Box>
            <Divider sx={{ mb: 1 }} />
            <Button
              size="small"
              variant="text"
              onClick={() => setShowHistory((v) => !v)}
              sx={{ textTransform: "none", px: 0 }}
            >
              {showHistory ? "Nascondi" : "Mostra"} cronologia conversioni ({history.length})
            </Button>
            {showHistory && (
              <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column" }}>
                {history.map((h) => {
                  const isDeleting = deletingId === h.job_id;
                  const isEditable = h.status === "done" || h.status === "failed" || h.status === "cancelled";
                  return (
                    <Box
                      key={h.job_id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 0.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        opacity: isDeleting ? 0.5 : 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1, display: "flex", alignItems: "baseline", gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap title={h.filename}>
                          {h.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                          {new Date(h.created_at).toLocaleString("it-IT", {
                            day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
                          })}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={
                          h.status === "done"
                            ? "Completata"
                            : h.status === "failed"
                              ? "Non riuscita"
                              : h.status === "cancelled"
                                ? "Interrotta"
                                : h.stage_label
                        }
                        color={statusChipColor(h.status)}
                        sx={{ height: 20, fontSize: "0.68rem", flexShrink: 0 }}
                      />
                      {h.can_resume && (
                        <Tooltip title="Riprendi da dove si era fermata">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleResume(h)}
                              disabled={isDeleting || resumingId === h.job_id || isActive}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {h.can_download && (
                        <Tooltip title="Scarica Excel">
                          <IconButton size="small" onClick={() => handleDownload(h)} disabled={isDeleting}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={isEditable ? "Elimina definitivamente" : "Attendi il completamento"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(h)}
                            disabled={!isEditable || isDeleting}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <PdfExtractionReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        report={job?.report ?? null}
        filename={job?.filename}
      />
    </>
  );
};
